import Alpine from './alpine'

/**
 * The condition and action builder.
 *
 * Keeps a blank row on each list so adding the next one is not a click on "add"
 * first, and hides actions that cannot apply to the chosen trigger - offering a rule
 * that can never run is worse than offering nothing.
 */
Alpine.data('automationBuilder', (config = {}) => ({
    trigger: config.trigger ?? '',
    triggers: config.triggers ?? {},
    actionTypes: config.actionTypes ?? {},
    conditionFields: config.conditionFields ?? {},

    conditions: [],
    actions: [],

    init() {
        this.conditions = (config.conditions ?? []).map((row) => ({
            field: row.field ?? '',
            operator: row.operator ?? 'equals',
            value: row.value ?? '',
        }))

        this.actions = (config.actions ?? []).map((row) => ({
            type: row.type ?? '',
            value: row.value ?? '',
        }))

        this.ensureBlankCondition()
        this.ensureBlankAction()
    },

    /** What the trigger hands to the actions: an order, a customer, a cart. */
    get subject() {
        return this.triggers[this.trigger]?.subject ?? 'order'
    },

    /** Only fields this trigger's subject actually carries. */
    get availableFields() {
        return Object.entries(this.conditionFields)
            .filter(([, field]) => field.subjects.includes(this.subject))
            .map(([key, field]) => ({ key, label: field.label }))
    },

    get availableActions() {
        return Object.entries(this.actionTypes)
            .filter(([, action]) => action.subjects.includes(this.subject))
            .map(([key, action]) => ({ key, label: action.label, field: action.field }))
    },

    fieldFor(type) {
        return this.actionTypes[type]?.field ?? null
    },

    addCondition() {
        this.conditions.push({ field: '', operator: 'equals', value: '' })
    },

    removeCondition(index) {
        this.conditions.splice(index, 1)
        this.ensureBlankCondition()
    },

    ensureBlankCondition() {
        const last = this.conditions[this.conditions.length - 1]

        if (! last || last.field !== '') {
            this.addCondition()
        }
    },

    addAction() {
        this.actions.push({ type: '', value: '' })
    },

    removeAction(index) {
        this.actions.splice(index, 1)
        this.ensureBlankAction()
    },

    ensureBlankAction() {
        const last = this.actions[this.actions.length - 1]

        if (! last || last.type !== '') {
            this.addAction()
        }
    },

    /*
     * Changing the trigger drops anything that no longer applies, rather than leaving
     * a rule that looks configured and silently cannot run.
     */
    triggerChanged() {
        const fields = this.availableFields.map((field) => field.key)
        const actions = this.availableActions.map((action) => action.key)

        this.conditions = this.conditions.filter((row) => row.field === '' || fields.includes(row.field))
        this.actions = this.actions.filter((row) => row.type === '' || actions.includes(row.type))

        this.ensureBlankCondition()
        this.ensureBlankAction()
    },
}))
